/**
 * ReconciliationImportWizard
 *
 * Wizard for importing CSV bank statements into a reconciliation session.
 * Steps: File Configuration -> Column Mapping -> Preview & Import
 *
 * Simplified version of ImportPage, focused on just: date, description, amount
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { importService } from '../../services/import.service';
import type {
  ParseCsvResponse,
  DateFormat,
  CsvDelimiter,
} from '@budgira/shared';
import { DATE_FORMATS, CSV_DELIMITERS } from '@budgira/shared';

// Target fields for reconciliation (simplified)
type ReconciliationTargetField = 'date' | 'description' | 'amount' | 'ignore' | null;

// Column mapping for reconciliation
interface ReconciliationColumnMapping {
  sourceColumn: number;
  targetField: ReconciliationTargetField;
}

// Parsed line ready for import
export interface ParsedReconciliationLine {
  lineNumber: number;
  date: string;
  description: string;
  amount: number;
  rawData: string[];
}

// Props for the wizard
interface ReconciliationImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (lines: ParsedReconciliationLine[]) => Promise<void>;
  sessionName?: string;
  accountName?: string;
}

type WizardStep = 'file' | 'columns' | 'preview';

const ReconciliationImportWizard: React.FC<ReconciliationImportWizardProps> = ({
  isOpen,
  onClose,
  onImport,
  sessionName,
  accountName,
}) => {
  const { t } = useTranslation();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('file');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // File state
  const [fileContent, setFileContent] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  // Parse result state
  const [parseResult, setParseResult] = useState<ParseCsvResponse | null>(null);

  // Settings state
  const [delimiter, setDelimiter] = useState<CsvDelimiter>(';');
  const [dateFormat, setDateFormat] = useState<DateFormat>('DD/MM/YYYY');
  const [hasHeader, setHasHeader] = useState(true);
  const [skipRows, setSkipRows] = useState(0);
  const [signedAmounts, setSignedAmounts] = useState(true);

  // Column mapping state
  const [columnMapping, setColumnMapping] = useState<ReconciliationColumnMapping[]>([]);

  // Parsed lines for preview
  const [parsedLines, setParsedLines] = useState<ParsedReconciliationLine[]>([]);

  // Reset wizard state
  const resetWizard = useCallback(() => {
    setCurrentStep('file');
    setFileContent('');
    setFileName('');
    setParseResult(null);
    setDelimiter(';');
    setDateFormat('DD/MM/YYYY');
    setHasHeader(true);
    setSkipRows(0);
    setSignedAmounts(true);
    setColumnMapping([]);
    setParsedLines([]);
    setError(null);
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    resetWizard();
    onClose();
  }, [resetWizard, onClose]);

  // Handle file upload
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsLoading(true);

    try {
      // Read file as ArrayBuffer to handle encoding detection
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Check for UTF-8 BOM
      const hasUtf8Bom = bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF;

      let text: string;

      if (hasUtf8Bom) {
        text = new TextDecoder('utf-8').decode(buffer);
      } else {
        // Try UTF-8 first
        const utf8Text = new TextDecoder('utf-8').decode(buffer);

        // Check for replacement character which indicates invalid UTF-8
        if (utf8Text.includes('\uFFFD')) {
          // UTF-8 failed, try Windows-1252 (common for French CSV files)
          text = new TextDecoder('windows-1252').decode(buffer);
        } else {
          text = utf8Text;
        }
      }

      setFileContent(text);
      setFileName(file.name);

      // Parse CSV
      const result = await importService.parseCsv({
        content: text,
        hasHeader: true,
      });

      setParseResult(result);
      setDelimiter(result.detectedDelimiter);
      setDateFormat(result.detectedDateFormat);

      // Initialize column mapping with auto-detection
      const initialMapping: ReconciliationColumnMapping[] = result.headers.map((header, index) => {
        const headerLower = header.toLowerCase();
        let targetField: ReconciliationTargetField = null;

        // Auto-detect based on header names
        if (headerLower.includes('date') && !headerLower.includes('valeur')) {
          targetField = 'date';
        } else if (
          headerLower.includes('libellé') ||
          headerLower.includes('libelle') ||
          headerLower.includes('description') ||
          headerLower.includes('détail') ||
          headerLower.includes('detail')
        ) {
          targetField = 'description';
        } else if (
          headerLower.includes('montant') ||
          headerLower.includes('amount') ||
          headerLower.includes('debit') ||
          headerLower.includes('crédit') ||
          headerLower.includes('credit')
        ) {
          targetField = 'amount';
        }

        return { sourceColumn: index, targetField };
      });

      // Ensure only one of each required field is selected
      const usedFields = new Set<ReconciliationTargetField>();
      const finalMapping = initialMapping.map((m) => {
        if (m.targetField && m.targetField !== 'ignore') {
          if (usedFields.has(m.targetField)) {
            return { ...m, targetField: null };
          }
          usedFields.add(m.targetField);
        }
        return m;
      });

      setColumnMapping(finalMapping);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('import.errors.parseFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Re-parse when settings change
  const handleReparse = useCallback(async () => {
    if (!fileContent) return;

    setError(null);
    setIsLoading(true);

    try {
      const result = await importService.parseCsv({
        content: fileContent,
        delimiter,
        hasHeader,
        skipRows,
      });

      setParseResult(result);

      // Re-initialize column mapping
      const initialMapping: ReconciliationColumnMapping[] = result.headers.map((_, index) => ({
        sourceColumn: index,
        targetField: null,
      }));
      setColumnMapping(initialMapping);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('import.errors.parseFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [fileContent, delimiter, hasHeader, skipRows, t]);

  // Handle column mapping change
  const handleColumnMappingChange = useCallback(
    (sourceColumn: number, targetField: ReconciliationTargetField) => {
      setColumnMapping((prev) => {
        return prev.map((m) => {
          if (m.sourceColumn === sourceColumn) {
            return { ...m, targetField };
          }
          // If another column had this target (except 'ignore'), clear it
          if (targetField && targetField !== 'ignore' && m.targetField === targetField) {
            return { ...m, targetField: null };
          }
          return m;
        });
      });
    },
    []
  );

  // Parse date string to ISO format
  const parseDate = useCallback(
    (dateStr: string): string | null => {
      if (!dateStr) return null;

      const cleanDate = dateStr.trim();
      let day: string, month: string, year: string;

      // Try parsing based on selected format
      const separatorMatch = cleanDate.match(/[\/\-\.]/);
      const separator = separatorMatch ? separatorMatch[0] : '/';
      const parts = cleanDate.split(separator);

      if (parts.length !== 3) return null;

      switch (dateFormat) {
        case 'DD/MM/YYYY':
        case 'DD-MM-YYYY':
        case 'DD.MM.YYYY':
          day = parts[0]!.padStart(2, '0');
          month = parts[1]!.padStart(2, '0');
          year = parts[2]!;
          break;
        case 'MM/DD/YYYY':
          month = parts[0]!.padStart(2, '0');
          day = parts[1]!.padStart(2, '0');
          year = parts[2]!;
          break;
        case 'YYYY-MM-DD':
          year = parts[0]!;
          month = parts[1]!.padStart(2, '0');
          day = parts[2]!.padStart(2, '0');
          break;
        case 'YYYYMMDD':
          // Special case: no separator
          if (cleanDate.length === 8) {
            year = cleanDate.substring(0, 4);
            month = cleanDate.substring(4, 6);
            day = cleanDate.substring(6, 8);
          } else {
            return null;
          }
          break;
        default:
          return null;
      }

      // Handle 2-digit years
      if (year.length === 2) {
        year = '20' + year;
      }

      // Validate
      const numMonth = parseInt(month, 10);
      const numDay = parseInt(day, 10);
      if (numMonth < 1 || numMonth > 12 || numDay < 1 || numDay > 31) {
        return null;
      }

      return `${year}-${month}-${day}`;
    },
    [dateFormat]
  );

  // Parse amount string to number
  const parseAmount = useCallback(
    (amountStr: string): number | null => {
      if (!amountStr) return null;

      // Clean the amount string
      let cleaned = amountStr
        .replace(/EUR/gi, '')
        .replace(/€/g, '')
        .replace(/\s/g, '')
        .trim();

      // Handle French format (comma as decimal separator)
      // If there's both comma and dot, assume comma is decimal
      if (cleaned.includes(',') && cleaned.includes('.')) {
        // Remove thousand separator (dot) and use comma as decimal
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else if (cleaned.includes(',')) {
        cleaned = cleaned.replace(',', '.');
      }

      const amount = parseFloat(cleaned);
      return isNaN(amount) ? null : amount;
    },
    []
  );

  // Generate parsed lines from CSV data
  const generateParsedLines = useCallback((): ParsedReconciliationLine[] => {
    if (!parseResult) return [];

    const dateCol = columnMapping.find((m) => m.targetField === 'date')?.sourceColumn;
    const descCol = columnMapping.find((m) => m.targetField === 'description')?.sourceColumn;
    const amountCol = columnMapping.find((m) => m.targetField === 'amount')?.sourceColumn;

    if (dateCol === undefined || descCol === undefined || amountCol === undefined) {
      return [];
    }

    const lines: ParsedReconciliationLine[] = [];
    const dataStartIndex = hasHeader ? 1 : 0;

    // Use all rows from the file (parseResult.totalRows), not just preview
    // We need to re-parse the file to get all rows
    const allRows = fileContent
      .split('\n')
      .filter((row) => row.trim())
      .slice(skipRows); // Apply skipRows

    // Skip header if needed
    const dataRows = hasHeader ? allRows.slice(1) : allRows;

    dataRows.forEach((rowStr, index) => {
      // Parse the row using the same logic as the backend
      const row = parseRowString(rowStr, delimiter);

      const dateStr = row[dateCol] || '';
      const description = row[descCol] || '';
      const amountStr = row[amountCol] || '';

      const date = parseDate(dateStr);
      const amount = parseAmount(amountStr);

      if (date && amount !== null) {
        lines.push({
          lineNumber: index + dataStartIndex + skipRows + 1,
          date,
          description: description.trim(),
          amount: signedAmounts ? amount : Math.abs(amount),
          rawData: row,
        });
      }
    });

    return lines;
  }, [parseResult, columnMapping, fileContent, hasHeader, skipRows, delimiter, parseDate, parseAmount, signedAmounts]);

  // Helper to parse a CSV row string
  const parseRowString = (row: string, sep: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;

    for (const char of row) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === sep && !inQuotes) {
        fields.push(current.trim().replace(/^"|"$/g, '').replace(/^="|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    fields.push(current.trim().replace(/^"|"$/g, '').replace(/^="|"$/g, ''));
    return fields;
  };

  // Validate column mapping
  const isColumnMappingValid = useMemo(() => {
    const hasDate = columnMapping.some((m) => m.targetField === 'date');
    const hasDescription = columnMapping.some((m) => m.targetField === 'description');
    const hasAmount = columnMapping.some((m) => m.targetField === 'amount');
    return hasDate && hasDescription && hasAmount;
  }, [columnMapping]);

  // Handle next step
  const handleNext = useCallback(async () => {
    setError(null);

    if (currentStep === 'file') {
      if (!parseResult) {
        setError(t('import.errors.noFile', 'Veuillez sélectionner un fichier'));
        return;
      }
      setCurrentStep('columns');
    } else if (currentStep === 'columns') {
      if (!isColumnMappingValid) {
        setError(
          t(
            'reconciliation.import.missingFields',
            'Veuillez mapper les colonnes Date, Description et Montant'
          )
        );
        return;
      }

      // Generate preview
      const lines = generateParsedLines();
      if (lines.length === 0) {
        setError(
          t(
            'reconciliation.import.noValidLines',
            'Aucune ligne valide trouvée. Vérifiez le format de date et les montants.'
          )
        );
        return;
      }
      setParsedLines(lines);
      setCurrentStep('preview');
    }
  }, [currentStep, parseResult, isColumnMappingValid, generateParsedLines, t]);

  // Handle back step
  const handleBack = useCallback(() => {
    if (currentStep === 'columns') {
      setCurrentStep('file');
    } else if (currentStep === 'preview') {
      setCurrentStep('columns');
    }
  }, [currentStep]);

  // Handle import
  const handleImport = useCallback(async () => {
    if (parsedLines.length === 0) return;

    setIsLoading(true);
    setError(null);

    try {
      await onImport(parsedLines);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reconciliation.import.failed', 'Échec de l\'import'));
    } finally {
      setIsLoading(false);
    }
  }, [parsedLines, onImport, handleClose, t]);

  // Format currency for preview
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Step indicator
  const steps: { key: WizardStep; label: string }[] = [
    { key: 'file', label: t('reconciliation.import.steps.file', 'Fichier') },
    { key: 'columns', label: t('reconciliation.import.steps.columns', 'Colonnes') },
    { key: 'preview', label: t('reconciliation.import.steps.preview', 'Aperçu') },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  // Target field options
  const targetFieldOptions: { value: ReconciliationTargetField; label: string }[] = [
    { value: null, label: t('reconciliation.import.fields.notMapped', '-- Non mappé --') },
    { value: 'date', label: t('reconciliation.import.fields.date', 'Date') },
    { value: 'description', label: t('reconciliation.import.fields.description', 'Description') },
    { value: 'amount', label: t('reconciliation.import.fields.amount', 'Montant') },
    { value: 'ignore', label: t('reconciliation.import.fields.ignore', 'Ignorer') },
  ];

  // Get available options for a specific column (exclude already used fields)
  const getAvailableOptions = useCallback(
    (sourceColumn: number) => {
      const currentMapping = columnMapping.find((m) => m.sourceColumn === sourceColumn);
      const currentField = currentMapping?.targetField;

      // Get all fields already used by OTHER columns
      const usedFields = new Set<ReconciliationTargetField>();
      columnMapping.forEach((m) => {
        if (m.sourceColumn !== sourceColumn && m.targetField && m.targetField !== 'ignore') {
          usedFields.add(m.targetField);
        }
      });

      // Filter options to show only available ones + current selection + ignore + null
      return targetFieldOptions.filter((opt) => {
        // Always show "Non mappé" and "Ignorer"
        if (opt.value === null || opt.value === 'ignore') return true;
        // Always show the current selection
        if (opt.value === currentField) return true;
        // Hide if already used by another column
        return !usedFields.has(opt.value);
      });
    },
    [columnMapping, targetFieldOptions]
  );

  if (!isOpen) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('reconciliation.import.title', 'Importer un relevé bancaire')}
              </h1>
              {(sessionName || accountName) && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {sessionName}{sessionName && accountName && ' • '}{accountName}
                </p>
              )}
            </div>
          </div>

          {/* Step indicator - desktop */}
          <div className="hidden sm:flex items-center">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                <div className="flex items-center">
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${
                        index <= currentStepIndex
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }
                    `}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={`ml-2 text-sm ${
                      index <= currentStepIndex
                        ? 'text-gray-900 dark:text-white font-medium'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-8 h-0.5 mx-2 ${
                      index < currentStepIndex
                        ? 'bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step indicator - mobile */}
        <div className="sm:hidden mt-3 flex items-center justify-center gap-2">
          {steps.map((step, index) => (
            <div
              key={step.key}
              className={`h-2 rounded-full transition-all ${
                index === currentStepIndex
                  ? 'w-8 bg-primary-600'
                  : index < currentStepIndex
                  ? 'w-2 bg-primary-600'
                  : 'w-2 bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="w-full px-4 mt-4">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6">
          {/* Step 1: File */}
          {currentStep === 'file' && (
            <div className="space-y-6">
              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('reconciliation.import.selectFile', 'Sélectionner un fichier CSV')}
                </label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary-50 file:text-primary-700
                    dark:file:bg-primary-900 dark:file:text-primary-300
                    hover:file:bg-primary-100 dark:hover:file:bg-primary-800
                    cursor-pointer"
                />
                {fileName && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {t('reconciliation.import.selectedFile', 'Fichier sélectionné')}: {fileName}
                  </p>
                )}
              </div>

              {/* Settings */}
              {parseResult && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('import.upload.delimiter', 'Séparateur')}
                      </label>
                      <select
                        value={delimiter}
                        onChange={(e) => setDelimiter(e.target.value as CsvDelimiter)}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      >
                        {CSV_DELIMITERS.map((d) => (
                          <option key={d} value={d}>
                            {d === ';'
                              ? t('import.delimiters.semicolon', 'Point-virgule (;)')
                              : d === ','
                              ? t('import.delimiters.comma', 'Virgule (,)')
                              : d === '\t'
                              ? t('import.delimiters.tab', 'Tabulation')
                              : t('import.delimiters.pipe', 'Pipe (|)')}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('import.upload.dateFormat', 'Format de date')}
                      </label>
                      <select
                        value={dateFormat}
                        onChange={(e) => setDateFormat(e.target.value as DateFormat)}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      >
                        {DATE_FORMATS.map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('import.upload.skipRows', 'Lignes à ignorer')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={skipRows}
                        onChange={(e) => setSkipRows(Math.max(0, parseInt(e.target.value) || 0))}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      />
                    </div>

                    <div className="flex items-end">
                      <Button onClick={handleReparse} variant="secondary" size="sm" disabled={isLoading}>
                        {t('import.upload.reparse', 'Réanalyser')}
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={hasHeader}
                        onChange={(e) => setHasHeader(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('import.upload.hasHeader', 'La première ligne contient les en-têtes')}
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={signedAmounts}
                        onChange={(e) => setSignedAmounts(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {t('import.upload.signedAmounts', 'Les montants sont signés (négatif = débit)')}
                      </span>
                    </label>
                  </div>

                  {/* Preview table */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('reconciliation.import.preview', 'Aperçu du fichier')} ({parseResult.totalRows}{' '}
                      {t('reconciliation.import.lines', 'lignes')})
                    </h3>
                    <div className="overflow-x-auto border rounded-lg dark:border-gray-600">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                          <tr>
                            {parseResult.headers.map((header, i) => (
                              <th
                                key={i}
                                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                              >
                                {header || `Col ${i + 1}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {parseResult.previewRows.slice(0, 10).map((row, i) => (
                            <tr key={i}>
                              {row.map((cell, j) => (
                                <td
                                  key={j}
                                  className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap"
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Column mapping */}
          {currentStep === 'columns' && parseResult && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'reconciliation.import.columnsDescription',
                  'Associez les colonnes du fichier CSV aux champs requis : Date, Description et Montant.'
                )}
              </p>

              {/* Mapping status */}
              <div className="flex flex-wrap gap-2 text-sm">
                <span
                  className={`px-3 py-1.5 rounded-lg font-medium ${
                    columnMapping.some((m) => m.targetField === 'date')
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  Date {columnMapping.some((m) => m.targetField === 'date') ? '✓' : '✗'}
                </span>
                <span
                  className={`px-3 py-1.5 rounded-lg font-medium ${
                    columnMapping.some((m) => m.targetField === 'description')
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  Description {columnMapping.some((m) => m.targetField === 'description') ? '✓' : '✗'}
                </span>
                <span
                  className={`px-3 py-1.5 rounded-lg font-medium ${
                    columnMapping.some((m) => m.targetField === 'amount')
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  Montant {columnMapping.some((m) => m.targetField === 'amount') ? '✓' : '✗'}
                </span>
              </div>

              <div className="overflow-x-auto border rounded-lg dark:border-gray-600">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('reconciliation.import.sourceColumn', 'Colonne source')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('reconciliation.import.sampleValues', 'Exemples')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('reconciliation.import.targetField', 'Champ cible')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {parseResult.headers
                      .map((header, index) => ({ header, index }))
                      .filter(({ header }) => header && header.trim() !== '')
                      .map(({ header, index }) => {
                        const mapping = columnMapping.find((m) => m.sourceColumn === index);
                        const currentField = mapping?.targetField;
                        const sampleValues = parseResult.previewRows
                          .slice(0, 3)
                          .map((row) => row[index])
                          .filter(Boolean)
                          .join(' | ');

                        return (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {header}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-md truncate">
                              {sampleValues}
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={currentField || ''}
                                onChange={(e) =>
                                  handleColumnMappingChange(
                                    index,
                                    (e.target.value || null) as ReconciliationTargetField
                                  )
                                }
                                className={`w-full sm:w-48 rounded-md border px-3 py-2 text-sm ${
                                  currentField === 'date' ||
                                  currentField === 'description' ||
                                  currentField === 'amount'
                                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                                }`}
                              >
                                {getAvailableOptions(index).map((opt) => (
                                  <option key={opt.value || 'null'} value={opt.value || ''}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {currentStep === 'preview' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('reconciliation.import.previewDescription', 'Vérifiez les lignes avant l\'import.')}
                </p>
                <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  {parsedLines.length} {t('reconciliation.import.validLines', 'lignes valides')}
                </span>
              </div>

              <div className="overflow-x-auto border rounded-lg dark:border-gray-600">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        #
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('reconciliation.import.fields.date', 'Date')}
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('reconciliation.import.fields.description', 'Description')}
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        {t('reconciliation.import.fields.amount', 'Montant')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {parsedLines.slice(0, 100).map((line, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          {line.lineNumber}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {line.date}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                          {line.description}
                        </td>
                        <td
                          className={`px-3 py-2 text-sm text-right whitespace-nowrap font-medium ${
                            line.amount >= 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {formatAmount(line.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {parsedLines.length > 100 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  {t('reconciliation.import.showingFirst', 'Affichage des 100 premières lignes sur')}{' '}
                  {parsedLines.length}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-4">
        <div className="flex justify-between">
          <Button variant="secondary" onClick={currentStep === 'file' ? handleClose : handleBack}>
            {currentStep === 'file'
              ? t('common.cancel', 'Annuler')
              : t('common.back', 'Retour')}
          </Button>

          {currentStep === 'preview' ? (
            <Button variant="primary" onClick={handleImport} disabled={isLoading || parsedLines.length === 0}>
              {isLoading
                ? t('common.importing', 'Import en cours...')
                : t('reconciliation.import.importLines', `Importer ${parsedLines.length} lignes`)}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={isLoading || (currentStep === 'file' && !parseResult)}
            >
              {isLoading ? t('common.loading', 'Chargement...') : t('common.next', 'Suivant')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReconciliationImportWizard;
